import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import { Button, Loader } from '@c';
import { useActions } from '@h';
import { isEmptyScheme } from '@helps';
import { courseActions } from '@r/courses/courseSlice';
import { teacherActions } from '@r/teacher/teacherSlice';

import { Course } from '../TestView';
import { TestContextProvider } from '../utils';

import styles from './style.module.scss';

const TestPreview = ({
    schemeId = null,
    type = null,
    onTypeChange = () => {},
    headerExtra = null,
    onReturnToEditor = null,
}) => {
    const history = useHistory();

    const { currentNode } = useSelector(({ course }) => course);
    const { currentNodeStart, setCurrentTheme } = useActions(courseActions);
    const {
        scheme: teacherScheme,
        schemeId: teacherSchemeId,
        isLoading: teacherSchemeLoading,
        courses: teacherCourses,
        courseName,
    } = useSelector(({ teacher }) => teacher);
    const { schemeStart } = useActions(teacherActions);

    useEffect(() => {
        if (schemeId && teacherSchemeId !== schemeId) {
            schemeStart(schemeId);
        }
    }, [schemeId, teacherSchemeId, schemeStart]);

    useEffect(() => {
        setCurrentTheme(schemeId);
    }, [schemeId, setCurrentTheme]);

    const isEmpty = useMemo(() => {
        if (teacherSchemeId !== schemeId) {
            return false;
        }

        return isEmptyScheme(teacherScheme);
    }, [teacherScheme, teacherSchemeId, schemeId]);

    const isSchemeLoaded =
        Boolean(schemeId) && teacherSchemeId === schemeId && !!teacherScheme;

    useEffect(() => {
        if (schemeId && type !== null && isSchemeLoaded && !isEmpty) {
            currentNodeStart({ courseId: schemeId, type });
        }
    }, [schemeId, type, isSchemeLoaded, isEmpty, currentNodeStart]);

    const handleReturnToEditor = () => {
        if (onReturnToEditor) {
            onReturnToEditor();
            return;
        }

        history.push('/teacherhome');
    };

    const lessonMeta = useMemo(() => {
        const courses = teacherCourses || [];

        for (const course of courses) {
            for (const profile of course.children || []) {
                const leaf = (profile.children || []).find(
                    (child) => String(child.oid) === String(schemeId)
                );

                if (leaf) {
                    return {
                        courseName,
                        subject: course.title,
                        klass: profile.title,
                    };
                }
            }
        }

        return {
            courseName,
            subject: null,
            klass: null,
        };
    }, [teacherCourses, schemeId, courseName]);

    return (
        <TestContextProvider>
            <div className={styles.header}>
                {schemeId ? (
                    isEmpty ? (
                        <span className={styles.warning}>Урок пуст</span>
                    ) : (
                        <span>Тестовое воспроизведение</span>
                    )
                ) : (
                    <span className={styles.warning}>
                        Необходимо выбрать курс
                    </span>
                )}
                {schemeId && !isEmpty && (
                    <div className={styles.type_select_container}>
                        <span>Для типа восприятия: </span>
                        <FormControl variant="standard">
                            <Select
                                className={styles.type_select}
                                disableUnderline={true}
                                labelId="typeSelectLabel"
                                value={type || 'null'}
                                onChange={({ target: { value } }) => {
                                    onTypeChange(value);
                                }}
                            >
                                <MenuItem value="null">
                                    <em>Не выбрано</em>
                                </MenuItem>
                                <MenuItem value="Визуал">
                                    <span>Визуал</span>
                                </MenuItem>
                                <MenuItem value="Аудиал">
                                    <span>Аудиал</span>
                                </MenuItem>
                                <MenuItem value="Кинестетик">
                                    <span>Кинестетик</span>
                                </MenuItem>
                                <MenuItem value="Дискрет">
                                    <span>Дискрет</span>
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                )}
                {headerExtra}
            </div>
            {schemeId && !isSchemeLoaded ? (
                teacherSchemeLoading ? (
                    <div className={styles.loader_container}>
                        <Loader color="#007aff" />
                    </div>
                ) : null
            ) : isEmpty ? (
                <div className={styles.empty}>
                    <p>
                        Урок пуст. Добавьте блоки и нажмите Сохранить, чтобы
                        открыть превью.
                    </p>
                    <Button
                        className={styles.empty_button}
                        onClick={handleReturnToEditor}
                    >
                        Вернуться в редактор
                    </Button>
                </div>
            ) : (
                currentNode && (
                    <Course
                        type={type}
                        scheme={teacherScheme?.scheme || []}
                        lessonMeta={lessonMeta}
                        onReturnToEditor={handleReturnToEditor}
                    />
                )
            )}
        </TestContextProvider>
    );
};

export { TestPreview };
